import React, { useState, useEffect, useCallback } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { apiCall } from '../utils/auth';
import { API_ENDPOINTS } from '../config/clerkApi';
import { Flame, Award, Edit3 } from 'lucide-react';
import ContributionCalendar from './ContributionCalendar';
import { useActivityTracker, ACTIVITY_TYPES } from '../hooks/useActivityTracker';
import './ProfilePage.css';

const ProfilePage = () => {
  const { user: clerkUser, isSignedIn, isLoaded } = useUser();
  const { getToken } = useAuth();
  const { trackActivity, setRefreshCallback } = useActivityTracker();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [saveLoading, setSaveLoading] = useState(false);

  // Set up calendar refresh callback
  const handleCalendarRefresh = useCallback((refreshFn) => {
    setRefreshCallback(refreshFn);
  }, [setRefreshCallback]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !getToken) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      if (!isSignedIn) return;
      
      try {
        setLoading(true);
        setError('');
        
        const token = await getToken();
        
        if (!token) {
          setError('Authentication required');
          return;
        }
        
        const response = await apiCall(API_ENDPOINTS.USER_PROFILE, {}, getToken);
        
        if (response.ok) {
          const data = await response.json();
          setProfile(data);
          
          // Get real email from Clerk, fallback to profile email only if it's not a clerk_id email
          const clerkEmail = clerkUser?.emailAddresses?.[0]?.emailAddress;
          const realEmail = clerkEmail || (data.email && !data.email.includes('@clerk.local') ? data.email : '');
          
          setEditForm({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            email: realEmail,
            streak: data.streak || 0
          });
          
          // Track login activity
          trackActivity(ACTIVITY_TYPES.LOGIN);
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to load profile');
        }
      } catch (error) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [getToken, isLoaded, isSignedIn, clerkUser?.emailAddresses, trackActivity]);

  // Проверяем, что пользователь аутентифицирован
  if (!isLoaded) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <div className="loading-text">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="profile-page">
        <div className="error-container">
          <p className="error-message">Please sign in to view your profile</p>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      email: profile?.email || ''
    });
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      setError(null);

      const response = await apiCall(API_ENDPOINTS.USER_PROFILE, {
        method: 'PUT',
        body: JSON.stringify({
          first_name: editForm.first_name,
          last_name: editForm.last_name,
          // email убираем, так как он readonly
        })
      }, getToken);

      if (response.ok) {
        const updatedData = await response.json();
        setProfile(updatedData);
        setIsEditing(false);
        console.log('Profile updated successfully');
        
        // Track profile update activity
        trackActivity(ACTIVITY_TYPES.PROFILE_UPDATE);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <div className="loading-text">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="error-container">
          <p className="error-message">Error: {error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const displayName = profile?.full_name || profile?.email?.split('@')[0] || 'User';
  const initials = clerkUser?.firstName?.[0] || profile?.first_name?.[0] || profile?.email?.[0] || 'U';
  
  // Получаем аватар из Clerk (который может быть из Google) - используем те же свойства что и в navbar
  const avatarUrl = clerkUser?.imageUrl || clerkUser?.profileImageUrl;

  // Get real email from Clerk, fallback to profile email only if it's not a clerk_id email
  const getRealEmail = () => {
    // Try to get email from Clerk first
    const clerkEmail = clerkUser?.emailAddresses?.[0]?.emailAddress;
    if (clerkEmail) return clerkEmail;
    
    // Check if profile email is a fallback clerk_id email
    if (profile?.email && !profile.email.includes('@clerk.local')) {
      return profile.email;
    }
    
    // If no real email available
    return '';
  };

  const realEmail = getRealEmail();

  return (
    <div className="profile-page">
      <div className="profile-container">
        
        {/* Profile Header */}
        <div className="profile-header">
          <div className="avatar-large">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="avatar-image" />
            ) : (
              <span className="avatar-letter">{initials.toUpperCase()}</span>
            )}
          </div>
          
          <div className="user-info">
            <h1 className="user-name"><span className="purple-gradient-text">{displayName}</span></h1>
          </div>

          <div className="edit-controls">
            {!isEditing ? (
              <button onClick={handleEdit} className="edit-button">
                <Edit3 size={18} />
              </button>
            ) : (
              <div className="edit-actions">
                <button 
                  onClick={handleSave} 
                  disabled={saveLoading}
                  className="save-button"
                >
                  {saveLoading ? 'Saving...' : 'Save'}
                </button>
                <button onClick={handleCancel} className="cancel-button">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Current Streak */}
        <div className="streak-section">
          <div className="streak-card">
            <Flame className="streak-icon" />
            <div className="streak-info">
              <span className="streak-number">{profile?.current_streak || 0}</span>
              <span className="streak-label">Current Streak</span>
            </div>
          </div>
          
          <div className="streak-card">
            <Award className="streak-icon" />
            <div className="streak-info">
              <span className="streak-number">{profile?.max_streak || 0}</span>
              <span className="streak-label">Best Streak</span>
            </div>
          </div>
        </div>

        {/* Contribution Calendar */}
        <ContributionCalendar onRefresh={handleCalendarRefresh} />

        {/* User Information */}
        <div className="info-section">
          <h2><span className="purple-gradient-text">Personal Information</span></h2>
          
          {isEditing ? (
            <div className="edit-form">
              <div className="form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={editForm.first_name}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={editForm.last_name}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={realEmail}
                  readOnly
                  placeholder="Enter your email"
                  className="form-input readonly"
                />
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">First Name</span>
                <span className="info-value">{profile?.first_name || 'Not provided'}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Last Name</span>
                <span className="info-value">{profile?.last_name || 'Not provided'}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{realEmail || 'No email available'}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Member Since</span>
                <span className="info-value">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Unknown'}
                </span>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ProfilePage; 